import React from 'react';
import { Loader } from './icons';

// Figma "03 · Components / Button" levels. `tertiary` is the brand-book name for
// the text action; `ghost` is kept as an alias so existing call sites don't change.
// `accent` is the retro-blue button (Figma "Button-Blue"), for secondary calls to
// action that still need weight, such as "See how it works".
const VARIANT_CLASSES = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  tertiary: 'btn-ghost',
  ghost: 'btn-ghost',
  accent: 'btn-accent'
};

// Padding + touch-target floor per size. Utility classes win over the
// component-layer .btn-* padding/text-size via Tailwind's layer order.
// Labels are ≥16px (text-base) for legibility per the accessibility brief.
const SIZE_CLASSES = {
  sm: 'px-4 py-2.5 min-h-tap text-base',
  md: 'px-6 py-3 min-h-tap text-base',
  lg: 'px-10 py-4 min-h-tap text-base'
};

const Button = ({
  as: Component = 'button',
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'right',
  loading = false,
  disabled = false,
  className = '',
  children,
  ...props
}) => {
  const isDisabled = disabled || loading;
  const classes = [
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    'inline-flex items-center justify-center gap-2',
    className
  ].filter(Boolean).join(' ');

  return (
    <Component
      className={classes}
      disabled={Component === 'button' ? isDisabled : undefined}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        // Figma "Button/Loading": spinner ring before the label, label dimmed to 50%.
        <>
          <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />
          <span className="opacity-50">{children}</span>
          <span className="sr-only">Loading…</span>
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon className="w-4 h-4" />}
          {children}
          {Icon && iconPosition === 'right' && <Icon className="w-4 h-4" />}
        </>
      )}
    </Component>
  );
};

export default Button;
